import * as $ from 'jquery';
import { Lazy, serialDisposable, IDisposable, disposableSubscription, emptyDisposable } from 'common/barrels/core';
import { Subject, BehaviorSubject, Observable } from 'common/barrels/rx';
import { ControlArgs } from 'runtime/controls/controlargs';
import { redrawRequired, scrolled, click, filteredKeys } from 'common/rxui/core';
import { newGuid, emptyGuid } from 'common/functions/guid';
import * as array from 'common/functions/array';
import { preserveScroll } from 'common/functions/scroll';
import { numberbox } from 'common/barrels/controls';
import { Map } from 'immutable';

import { BindingInfo } from 'runtime/metadata/bindinginfo';
import { GridColumnMetaData } from 'runtime/metadata/gridcolumnmetadata';
import { ContainerControl } from 'runtime/controls/containercontrol';
import { MethodRouter } from 'runtime/methodrouter';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { registerBinding, getElementBindings } from 'runtime/bindingengine';
import * as rxtable from 'common/rxui/rxtable';
import { blur } from 'common/rxui/core';

import 'runtime/styles/controls/datagrid.less';

const autoClearance = 8;
const headerAutoClearance = 16;
const minColumnWidth = 10;

export class DataGrid extends ContainerControl {
    private $inner: JQuery;

    private $table: JQuery;
    private $tbody: JQuery;
    private $thead: JQuery;

    private $pager: JQuery;

    private viewInfo = new Lazy(() => this.eventAggregator.publish(new ViewInfoRequested()));

    // disposables
    private headerDisposable = serialDisposable(null);
    private contentBindingDisposable = serialDisposable(null);
    private filterBindingDisposable = serialDisposable(null);
    private pagerDisposables: IDisposable;

    // trigger subjects
    private updateRequired = new Subject<{}>();
    private layoutUpdated = new Subject<{}>();
    private pageRequested = new BehaviorSubject<any>(1);
    private displayedPageNumber = new BehaviorSubject<number>(1);

    // data subjects
    private rowIds: BehaviorSubject<string[]>;
    private filteredRowIds = new BehaviorSubject<string[]>([]);
    private rowsToDisplay: BehaviorSubject<string[]>;
    private columnVisibility = new Subject<{ index: number, value: boolean }>();
    private pageSubject: BehaviorSubject<number>;

    // important state
    private gridContext: string;
    private renderedRowIds: string[];
    private renderedRows = Map<string, JQuery>();
    private rowIdsAtLastRender: string[];
    private renderedColumnVisibility = Map<number, boolean>();
    private filterConfigured: boolean;

    // metadata
    private headerBindings: BindingInfo[][];
    private cellTemplates: JQuery[];
    private columnMetadata: GridColumnMetaData[];

    constructor(args: ControlArgs) {
        super(args);

        this.headerBindings = this.extractHeaderBindings();
        this.cellTemplates = this.extractCellTemplates();

        this.$element.empty();

        var rowHeight = +this.config.rowHeight;
            
        this.$element.addClass('control-container datagrid');
        var $gridContainer = $('<div />').addClass('rxui-table datagrid-grid-container').appendTo(this.$element);
        var $outer = $('<div />').appendTo($gridContainer);
        var $headerBackground = $('<div />').addClass('rxui-table-header-background').height(this.config.headerHeight).appendTo($outer);
        this.$inner = $('<div />').addClass('rxui-table-inner').appendTo($outer);
        this.$table = $('<table />').appendTo(this.$inner);
        this.$tbody = $('<tbody />').appendTo(this.$table);
        rxtable.createChock().appendTo(this.$table);

        $outer.css('padding-top', this.config.headerHeight);

        this.columnMetadata = this.config.columns;

        this.pagerDisposables = this.paged() ? this.buildPager($gridContainer) : emptyDisposable;

        this.methods.register(this.config.elementName, this);

        this.rowIds = new BehaviorSubject([]);
        this.pageSubject = new BehaviorSubject(0);

        this.renderedRowIds = [];
        this.renderedRows = Map<string, JQuery>();

        this.applyTooltip(this.$element);

        var columnVisibility = this.columnVisibility
            .scan((a, x) => a.set(x.index, x.value), Map<number, boolean>())
            .filter(x => this.allColumnVisibilitiesProvided(x, this.columnMetadata))
            .distinctUntilChanged()
            .share()
            ;

        this.$thead = $();

        this.filterConfigured = (this.config.filterRule !== undefined && this.config.filterRule !== '');

        this.rowsToDisplay = this.filterConfigured ? this.filteredRowIds : this.rowIds;

        this.subscriptions = disposableSubscription(
            redrawRequired(this.updateRequired, this.activatedStream, this.visibilityStream, this.stretches() ? this.container.resizeStream() : Observable.never())
                .withLatestFrom(columnVisibility, (x, y) => y)
                .subscribe(x => this.resize(x)),
            scrolled(this.$inner).switchMap(() => Observable.timer(0)).subscribe(() => rxtable.updateHeadingPositions(this.$table)),

            click(this.$table).subscribe(e => $(e.target).closest('tr').addClass('rxui-table-row-selected').siblings().removeClass('rxui-table-row-selected')),

            columnVisibility.subscribe(x => {
                this.$thead = this.buildHeader(this.columnMetadata, x, this.config.headerHeight);
                this.updateRequired.next({});
            }),

            columnVisibility
                .combineLatest(this.rowsToDisplay, (c, r) => ({ vis: c, rows: r }))
                .combineLatest(this.pageSubject.merge(this.pageRequested).distinctUntilChanged(), (x, p) => ({ vis: x.vis, rows: x.rows, page: p }))
                .subscribe(x => this.updateRows(x.rows, x.page, x.vis, rowHeight)),

            this.bindColumnVisibilities(this.columnMetadata),

            this.processBindings(this.config),
            this.managePrimaryBinding('content.GridField')
        );

        this.initialize();
    }

    private pageSize(): number { return this.config['content.PageSize']; }

    private paged(): boolean { return this.pageSize() > 0; }

    private allColumnVisibilitiesProvided(vis: Map<number, boolean>, meta: GridColumnMetaData[]) {
        return meta.every(x => vis.has(x.sequenceNumber));
    }

    private propagateEnabledStateToRows($rows: JQuery) {
        this.propagateEnabledStateToChildElements($rows.children().children(), this.writableAgnosticDerivedEnabledState());
    }

    dipose() {
        super.dispose();
        this.contentBindingDisposable.dispose();
        this.filterBindingDisposable.dispose();
        this.headerDisposable.dispose();
        this.pagerDisposables.dispose();
    }

    'content.GridField'(value) {
        this.checkInitialValueReceived();

        this.gridContext = value;
        this.contentBindingDisposable.set(this.values.bindOneWay('Field', this.gridContext, 'Rows',(values, identifier) => this.rows(identifier, values)));

        if (this.filterConfigured)
            this.filterBindingDisposable.set(this.values.bindOneWay('Rule', this.gridContext, this.config.filterRule, x => this.filteredRowIds.next(x === '' || x === undefined || x === null ? [] : (x).split(','))));
    }

    rows(fieldContext, values) {
        this.rowIds.next(values);
    }

    private buildPager($gridContainer: JQuery): IDisposable {
        $gridContainer.addClass('datagrid-grid-container-with-pager');

        this.$pager = $('<div />').addClass('datagrid-pager noselect').appendTo(this.$element);

        var showPage = x => this.pageRequested.next(x);

        var ifNotFirstPage = x => { if (this.pageSubject.getValue() !== 1) x() };
        var ifNotLastPage = x => { if (this.pageSubject.getValue() !== this.pageCount(this.rowsToDisplay.getValue())) x() };

        $('<span />')
            .append($('<i />').addClass('fa fa-caret-right'))
            .append($('<i />').addClass('fa fa-caret-right'))
            .on('click', () => ifNotLastPage(() => showPage('last')))
            .appendTo(this.$pager);

        $('<span />')
            .append($('<i />').addClass('fa fa-caret-right'))
            .on('click', () => ifNotLastPage(() => showPage('next')))
            .appendTo(this.$pager);
            
        $('<span />').addClass('datagrid-pager-pagecount').appendTo(this.$pager);

        const $page = numberbox(this.displayedPageNumber).appendTo(this.$pager);

        $('<span />').text(this.resources.getCoreString('Page')).appendTo(this.$pager);

        $('<span />')
            .append($('<i />').addClass('fa fa-caret-left'))
            .on('click', () => ifNotFirstPage(() => showPage('prev')))
            .appendTo(this.$pager);

        $('<span />')
            .append($('<i />').addClass('fa fa-caret-left'))
            .append($('<i />').addClass('fa fa-caret-left'))
            .on('click', () => ifNotFirstPage(() => showPage(1)))
            .appendTo(this.$pager);
        
        const pageTrigger = blur($page).merge(filteredKeys($page, [$.ui.keyCode.ENTER]));

        return disposableSubscription(pageTrigger.withLatestFrom(this.displayedPageNumber, (_, x) => x).subscribe(x => showPage(x)));
    }

    private updatePager(page: number, pageCount: number) {
        this.$pager.find('.datagrid-pager-pagecount').text(this.resources.getCoreString('PageOf') + ' ' + pageCount.toString());
        this.displayedPageNumber.next(page);

        this.$pager.find('.fa-caret-left').toggleClass('datagrid-pager-icon-disabled', page === 1);
        this.$pager.find('.fa-caret-right').toggleClass('datagrid-pager-icon-disabled', page === pageCount);
    }

    private extractCellTemplates() {
        const comparer = (a, b) => a.binding.columnIndex - b.binding.columnIndex;
        return getElementBindings(this.$element.children(), 'datagridcellcontainer').sort(comparer).map(x => x.element);
    }

    private extractHeaderBindings() {
        return getElementBindings(this.$element.children(), 'datagridcolumnheadercell').map(x => x.binding).map(x => x.bindings);
    }

    private bindColumnVisibilities(columns: GridColumnMetaData[]): IDisposable {
        var setColumnVisibility = (seq, value) => this.columnVisibility.next({ index: seq, value: value });

        this.headerBindings.map((x, i) => ({ i: i, hasBindings: x.some(y => y.target === 'IsElementVisible') })).filter(x => !x.hasBindings).forEach(x => setColumnVisibility(x.i, true));

        const invertIfNecessary = (p, x) => (x === null || p !== 'invert') ? x : !x;

        const ignoreContextIfNecessary = (p, x) => p === 'true' ? emptyGuid : x;

        return disposableSubscription(...this.headerBindings
            .map((x, i) => disposableSubscription(
                ...x.filter(y => y.target === 'IsElementVisible').map(y => this.values.bindOneWay(y.type, ignoreContextIfNecessary(y.ignoreFieldContext, this.fieldContext), y.source, v => setColumnVisibility(i, invertIfNecessary(y.parameter, v))))
            )));
    }

    private buildHeader(columns: GridColumnMetaData[], vis: Map<number, boolean>, headerHeight: number) {
        this.$thead.remove();

        const result = $('<thead />')
            .addClass('noselect')
            .append(array.zip(columns, this.headerBindings, (x, y) => ({ meta: x, bindings: y })).filter(x => vis.get(x.meta.sequenceNumber) === true).reduce((a, x) => a.append(this.makeHeaderCell(x.meta, x.bindings, headerHeight)), $('<tr />'))
            .append(this.makeHeaderFillerCell(headerHeight))).appendTo(this.$table);

        // not awesome, but the disposal clean up disrupts freshly-added styles (arguably a bug in the makeHeadersDraggable call)
        this.headerDisposable.set(null);

        var vismeta = columns.filter(x => vis.get(x.sequenceNumber) === true);

        this.headerDisposable.set(disposableSubscription(
            this.config.allowColumnResizing ? rxtable.makeColumnsResizable(this.$table).subscribe(x => {
                var column = vismeta[x.index];
                column.sizeType = 'pixel';
                column.size = Math.max(x.size, minColumnWidth);
                this.updateRequired.next({});
            }) : emptyDisposable,

            this.config.allowColumnReordering ? rxtable.makeHeadersDraggable(this.$table).subscribe(x => this.moveColumn(x.oldIndex, x.newIndex, vis)) : emptyDisposable
        ));

        return result;
    }

    private makeHeaderCell(meta: GridColumnMetaData, bindings: BindingInfo[], headerHeight: number) {
        var $th = $('<th />');
        var $outer = $('<div />').height(headerHeight).appendTo($th);
        var $inner = $('<div />').addClass('datagrid-headertext').appendTo($outer);
        var $preWrapper = $('<pre>').appendTo($inner);

        // if using local strings and using an auto-sized sizeType, an update must be triggered on arrival of the string
        const triggerResizeIfNecessary = meta.sizeType === 'sizeToHeader' || meta.sizeType === 'auto'
            ? () => window.setTimeout(() => this.updateRequired.next({}))
            : () => { };

        const text = meta.languageKey
            ? this.resources.getString(this.viewInfo.value().definitionName, meta.languageKey).do(_ => triggerResizeIfNecessary())
            : meta.header;

        var $headerCell = $('<span />')
            .bindText(text)
            .appendTo($preWrapper);
            
        var setHeaderText = x => $headerCell.text(x === null ? '' : x);

        var setColumnVisibility = x => this.columnVisibility.next({ index: meta.sequenceNumber, value: x });

        var disposables = disposableSubscription(...bindings.filter(x => x.target == 'Content.Text').map(b => this.values.bindOneWay(b.type, this.fieldContext, b.source, setHeaderText)));
            
        $th.on('remove', () => disposables.dispose());

        return $th;
    }

    private makeHeaderFillerCell(headerHeight: number) {
        return $('<th />').append($('<div />').height(headerHeight).addClass('rxui-table-filler'));
    }

    private applyColumnWidths(vis: Map<number, boolean>) {
        var numbered = this.columnMetadata.filter((x, i) => vis.get(x.sequenceNumber) === true).map((x, i) => ({ index: i, meta: x }));

        var nonStar = numbered
            .filter(x => x.meta.sizeType !== 'star')
            .map(x => ({ index: x.index, size: Math.max(this.getColumnSize(x.meta.sizeType, x.index, x.meta.size), minColumnWidth) }));

        var availableSpace = this.$table.width();

        var starSpace = availableSpace - array.sum(nonStar.map(x => x.size));
        var rawStar = numbered.filter(x => x.meta.sizeType === 'star').map(x => ({ index: x.index, stars: x.meta.size }));
        var starTotal = array.sum(rawStar.map(x => x.stars));
        var star = rawStar.map(x => ({ index: x.index, size: Math.max(starSpace * x.stars / starTotal, minColumnWidth) }));

        nonStar.forEach(x => this.$thead.find('tr>th').eq(x.index).width(x.size).css('min-width', x.size));
        star.forEach(x => this.$thead.find('tr>th').eq(x.index).width(x.size));

        this.$table.css('min-width', array.sum(nonStar.map(x => x.size)) + (star.length * minColumnWidth));

        this.manageFiller();
    }

    private getHeaderWidth(index: number) {
        return array.maxDefaultZero(this.$thead.find('tr>th').eq(index).find('span').toArray().map(x => $(x).width())) + headerAutoClearance;
    }

    private getMaxCellWidth(index: number) {
        return array.maxDefaultZero(this.$tbody.find('tr').toArray()
            .map(x => $(x).find('td').eq(index))
            .map(x => x.children().children().toArray().map(y => $(y).data('control').rightEdge()))
            .reduce((a, x) => a.concat(x), [])) + autoClearance;
    }

    private getColumnSize(sizeType: string, index: number, size: number) {
        return sizeType === 'sizeToHeader' ? this.getHeaderWidth(index)
                : sizeType === 'sizeToCells' ? this.getMaxCellWidth(index)
                : sizeType === 'auto' ? Math.max(this.getMaxCellWidth(index), this.getHeaderWidth(index))
                : size;
    }

    private manageFiller() {
        this.$table.find('.rxui-table-filler').toggleClass(
            'rxui-table-filler-noborder',
            array.maxDefaultZero(this.$tbody.find('.rxui-table-filler').toArray().map(x => $(x).width())) <= 2 && !(this.$inner[0].scrollHeight > this.$inner.height())
        );
    }

    private resize(vis: Map<number, boolean>) {
        var scrollLeftBefore = this.$inner.scrollLeft();
        var scrollTopBefore = this.$inner.scrollTop();
        this.applyColumnWidths(vis);
        rxtable.updateHeadingPositions(this.$table);
        rxtable.resizeInternal(this.$table);
        this.$inner.scrollLeft(scrollLeftBefore);
        this.$inner.scrollTop(scrollTopBefore);
        this.layoutUpdated.next({});
    }

    private buildRow(context: string, vis: Map<number, boolean>, rowHeight: number) {
        return array.zip(this.cellTemplates, this.columnMetadata, (x, y) => ({ template: x, meta: y }))
            .filter((x) => vis.get(x.meta.sequenceNumber) === true)
            .reduce((a, x) => {
                var $td = $('<td />').height(rowHeight).appendTo(a);
                var $templateElements = x.template.children().clone();

                var $cellContainer = $('<div />').addClass('datagrid-cellcontainer').append($templateElements).appendTo($td);
                this.installScrollVeto($cellContainer);
                var methods = new MethodRouter(this.methods, this.methods.viewName);
                $cellContainer.owns(methods);
                methods.register(this.config.elementName, { deleteRow: () => this.deleteRow(context) });

                this.bindElements($templateElements, this, context, methods);

                this.propagateEnabledStateToRows(a);

                a.data('context', context);

                return a;
            }, $('<tr />').height(rowHeight))
            .append(this.makeFillerCell());
    }

    private makeFillerCell() {
        return $('<td />').addClass('rxui-table-filler');
    }

    private pageCount(rowIds: any[]) {
        // inefficient, but can cache if it is any kind of problems
        return Math.max(1, Math.ceil(rowIds.length / this.pageSize()));
    }

    private setPage(rowIds: string[], desiredPage) {
        var newPageCount = this.pageCount(rowIds);

        var newPage = desiredPage === 'next' ? this.pageSubject.getValue() + 1
            : desiredPage === 'prev' ? this.pageSubject.getValue() - 1
            : desiredPage === 'last' ? newPageCount
            : desiredPage;

        if (newPage > newPageCount)
            newPage = newPageCount;

        if (newPage < 1)
            newPage = 1;

        this.pageSubject.next(newPage);

        this.updatePager(newPage, newPageCount);
    }

    private updateRows(
        newRows: string[],
        desiredPage,
        newVis: Map<number, boolean>,
        rowHeight: number
    ) {

        if (newVis !== this.renderedColumnVisibility) {
            this.renderedRowIds.forEach(x => {
                this.renderedRows.get(x).remove();
                this.renderedRows = this.renderedRows.remove(x);
            });
        }

        if (this.paged()) {
            const rowAddedToEnd = newRows !== this.rowIdsAtLastRender && array.sequenceEqual(newRows.slice(0, newRows.length - 1), this.rowIdsAtLastRender || []);
            this.setPage(newRows, rowAddedToEnd ? 'last' : desiredPage);
        }

        const rowsOnThisPage = !this.paged() ? newRows : newRows.slice((this.pageSubject.getValue() - 1) * this.pageSize(), this.pageSubject.getValue() * this.pageSize());

        array.except(this.renderedRowIds, ...rowsOnThisPage).forEach(x => {
            this.renderedRows.get(x).remove();
            this.renderedRows = this.renderedRows.remove(x);
        });

        const store = (k, v) => { this.renderedRows = this.renderedRows.set(k, v); return v; };

        preserveScroll(
            () => rowsOnThisPage.map(x => this.renderedRows.get(x) || store(x, this.buildRow(x, newVis, rowHeight))).forEach(x => this.$tbody.append(x)),
            this.$inner
        );

        this.$table.find('tr:odd').removeClass('rxui-table-row-even').addClass('rxui-table-row-odd');
        this.$table.find('tr:even').removeClass('rxui-table-row-odd').addClass('rxui-table-row-even');

        this.renderedRowIds = rowsOnThisPage;
        this.rowIdsAtLastRender = newRows;
        this.renderedColumnVisibility = newVis;
        this.updateRequired.next({});
    }

    addNewRow() {
        this.values.update('Field', this.gridContext, 'Rows', this.rowIds.getValue().concat(newGuid()));
    }

    deleteRow(fieldContext) {
        this.values.update('Field', this.gridContext, 'Rows', array.except(this.rowIds.getValue(), fieldContext));
        this.triggerEvent('AfterRowDeleted');
    }

    deleteSelectedRow() {
        var $row = this.$tbody.find('.rxui-table-row-selected');
        if ($row.length !== 1)
            return;

        var $rows = this.$tbody.find('tr');
        var i = $rows.index($row);
        this.deleteRow($row.data('context'));
        this.$tbody.find('tr').eq(Math.min(i, $rows.length - 2)).addClass('rxui-table-row-selected');
    }

    private moveColumn(oldIndex: number, newIndex: number, vis: Map<number, boolean>) {
        // this prevents placing columns after the filler
        if (newIndex === this.columnMetadata.filter(x => vis.get(x.sequenceNumber) === true).length)
            newIndex--;

        var move = oldIndex < newIndex
            ? (tr: JQuery) => tr.children().eq(oldIndex).insertAfter(tr.children().eq(newIndex))
            : (tr: JQuery) => tr.children().eq(oldIndex).insertBefore(tr.children().eq(newIndex));

        // reorder all existing cells (including headers)
        this.$table.find('tr').toArray().forEach(x => move($(x)));
        rxtable.updateHeadingPositions(this.$table);

        // reorder metadata
        this.columnMetadata = array.move(this.columnMetadata, oldIndex, newIndex);
        this.cellTemplates = array.move(this.cellTemplates, oldIndex, newIndex);
    }

    focus() {
        this.setFocusToFirstFocusableSubElement(this.$inner);
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToRows(this.$table.find('tr'));
    }

    tabIndexElements() {
        return this.$pager ? this.$pager.find('input') : $();
    }

    resizeStream(): Observable<{}> {
        return this.layoutUpdated;
    }
}

registerBinding('datagrid', DataGrid);

// dummy binding so the custom control mechanism doesn't mistake these for unknown / custom controls
registerBinding('datagridcellcontainer', undefined);
registerBinding('datagridcolumnheadercell', undefined);